import { Form, Formik } from 'formik';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { PrimaryButton } from '@components/Button/variants';
import DropdownField from '@components/Form/DropdownField';
import TextField from '@components/Form/TextField';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { AutocompleteOption } from '@utils/interfaces';

import { CorporateLeadFormProps, LeadTypes } from '.';
import { schema, schemaWithNumberOfUsers } from './schema';

const NUMBER_OF_USER_OPTIONS = [
  { label: 'Less than 10', value: '< 10' },
  { label: '10 - 25', value: '10 - 25' },
  { label: '26 - 100', value: '26 - 100' },
  { label: '100 - 1,000', value: '100 - 1000' },
  { label: 'Greater than 1,000', value: '> 1000' },
];

interface FormProps {
  companyName: string;
  email: string;
  jobTitle: string;
  leadType: LeadTypes;
  name: string;
  phoneNumber: string;
  possibleNumberOfUsers?: AutocompleteOption;
}

const INITIAL_VALUES: Omit<FormProps, 'leadType'> = {
  companyName: '',
  email: '',
  jobTitle: '',
  name: '',
  phoneNumber: '',
  possibleNumberOfUsers: {
    label: '',
    value: '',
  },
};

interface SubmitProps {
  companyName: string;
  email: string;
  jobTitle: string;
  name: string;
  phoneNumber: string;
  possibleNumberOfUsers?: AutocompleteOption;
}
interface CorporateLeadFormFieldsProps
  extends Omit<CorporateLeadFormProps, 'className' | 'getInTouchEmailAddress'> {
  onSubmit: (props: SubmitProps) => Promise<void>;
  reCaptchaRef: ReCAPTCHA;
}

const LeadForm = ({
  includeNumberOfUsers = true,
  leadType,
  onSubmit,
  reCaptchaRef,
}: CorporateLeadFormFieldsProps): JSX.Element => {
  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik
        validationSchema={includeNumberOfUsers ? schemaWithNumberOfUsers : schema}
        initialValues={INITIAL_VALUES}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="mt-16">
            <TextField name="name" placeholder="Full name" />
            <TextField className="mt-24" name="companyName" placeholder="Company" />
            <TextField className="mt-24" name="jobTitle" placeholder="Title" />
            <TextField className="mt-24" name="email" placeholder="Email" />
            <TextField className="mt-24" name="phoneNumber" placeholder="Phone number" />
            {includeNumberOfUsers && (
              <DropdownField
                className="mt-24"
                name="possibleNumberOfUsers"
                options={NUMBER_OF_USER_OPTIONS}
                placeholder="Potential Number of Users"
                isClearable={false}
              />
            )}
            <input name="leadType" value={leadType} hidden readOnly />
            <PrimaryButton
              as="button"
              className="mt-32"
              type="submit"
              disabled={formik.isSubmitting}
              isLoading={formik.isSubmitting}
              fullWidth
            >
              Contact us
            </PrimaryButton>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default LeadForm;
